import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Same reasoning as AuthContext: this provider wraps the whole app, so a
 * static import would pull axios into the main bundle for every visitor.
 */
let apiPromise;
const getApi = () => {
  if (!apiPromise) apiPromise = import('../utils/api').then((m) => m.default);
  return apiPromise;
};

const TechnicianAuthContext = createContext();

export const useTechnicianAuth = () => useContext(TechnicianAuthContext);

const TOKEN_KEY = 'technician_token';

const readToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;

const writeToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const TechnicianAuthProvider = ({ children }) => {
  const { t } = useTranslation('notifications');
  const [technician, setTechnician] = useState(null);
  const [token, setToken] = useState(readToken());
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (activeToken) => {
    try {
      const axios = await getApi();
      const res = await axios.get('/api/auth/me', {
        headers: { 'x-auth-token': activeToken },
      });

      // /me responds in one of two shapes — tolerate both:
      //   A) { success: true, data: { user: <Technician profile>, role: <jwt-decoded identity> } }
      //   B) <User document> with `profile` nested for techs (legacy)
      const body = res.data;
      const wrapped = body?.data;
      const identity = wrapped?.role || (body?._id ? body : null);
      const profile = wrapped?.user || body?.profile || null;

      if (!identity?._id || identity.role !== 'technician_marketplace') {
        throw new Error('Not a technician account');
      }

      setTechnician({
        ...(profile || {}),
        name: identity.name,
        email: identity.email,
        // Phone may live on User (legacy shape) or only on the profile (new shape).
        phone: profile?.phone || identity.phone || profile?.whatsappNumber || '',
        userId: identity._id,
      });
      setToken(activeToken);
      writeToken(activeToken);
      return true;
    } catch (err) {
      setTechnician(null);
      setToken(null);
      writeToken(null);
      return false;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const axios = await getApi();
      const res = await axios.post('/api/technician-auth/login', { email, password });
      if (res.data?.success && res.data.token) {
        const ok = await fetchMe(res.data.token);
        if (ok) toast.success(t('auth.welcomeBack'));
        return { success: ok };
      }
      throw new Error(res.data?.msg || 'Login failed');
    } catch (err) {
      const msg = err.response?.data?.msg || err.message || 'Login failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, [fetchMe, t]);

  const register = useCallback(async (payload) => {
    try {
      const axios = await getApi();
      const res = await axios.post('/api/technician-auth/register', payload);
      if (res.data?.success && res.data.token) {
        const ok = await fetchMe(res.data.token);
        if (ok) toast.success(t('auth.registered'));
        return { success: ok };
      }
      throw new Error(res.data?.msg || 'Registration failed');
    } catch (err) {
      const msg =
        err.response?.data?.errors?.[0]?.msg ||
        err.response?.data?.msg ||
        err.message ||
        'Registration failed';
      toast.error(msg);
      return { success: false, error: msg };
    }
  }, [fetchMe, t]);

  const logout = useCallback(() => {
    setTechnician(null);
    setToken(null);
    writeToken(null);
    toast.info(t('auth.loggedOut'));
  }, [t]);

  useEffect(() => {
    const stored = readToken();
    if (stored) {
      fetchMe(stored).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchMe]);

  const value = useMemo(
    () => ({ technician, token, loading, isAuthenticated: !!technician, login, register, logout, refresh: () => fetchMe(token) }),
    [technician, token, loading, login, register, logout, fetchMe]
  );

  return <TechnicianAuthContext.Provider value={value}>{children}</TechnicianAuthContext.Provider>;
};
